import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { DOCUMENT_VIEWER_CONTROL_MENU_BUTTON_TEST_ID } from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import type { DocumentViewerControlButtonProps } from './document-viewer-control-button';

export interface DocumentViewerControlMenuProps {
  label: string;
  icon: IconDefinition;
  testId: string;
  controls: Array<Omit<DocumentViewerControlButtonProps, 'icon'>>;
  loading: boolean;
}

export const DocumentViewerControlMenu = ({
  label,
  testId,
  icon,
  controls,
  loading,
}: DocumentViewerControlMenuProps) => {
  const telemetry = useTelemetry();
  const [processingControlLabel, setProcessingControlLabel] = useState<string | null>(null);

  return (
    <Menu as="div" className={tw`relative h-fit w-fit`} data-testid={testId}>
      <div>
        <MenuButton
          aria-label={label}
          data-testid={DOCUMENT_VIEWER_CONTROL_MENU_BUTTON_TEST_ID}
          className={tw`flex aspect-square cursor-pointer items-center justify-center rounded-full bg-content-main fill-white p-2 font-medium font-sans text-white shadow hover:bg-content-subtle/25 focus:outline-4 focus:outline-primary-main focus:outline-offset-[3px] focus-visible:outline-4 focus-visible:outline-primary-main focus-visible:outline-offset-[3px] active:scale-[.99] active:bg-content-subtle/25 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-content-disabled`}
        >
          {loading && <LoadingSpinner />}
          {!loading && <FontAwesomeIcon className={tw`h-5 w-5 text-white`} icon={icon} />}
        </MenuButton>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
        afterEnter={() =>
          telemetry.trackInteraction('document_viewer_control_menu_open', {
            controlMenuLabel: label,
          })
        }
        afterLeave={() =>
          telemetry.trackInteraction('document_viewer_control_menu_close', {
            controlMenuLabel: label,
          })
        }
      >
        <MenuItems
          className={tw`absolute top-6 left-3 z-menu w-max origin-top-left rounded-md bg-white px-1 py-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
        >
          {controls.map((control) => (
            <MenuItem key={control.label}>
              {({ close }) => (
                <Button
                  testId={control.testId}
                  type="button"
                  variant="link"
                  className={tw`flex items-center justify-center gap-2 px-4 py-2 text-content-subtle text-sm transition-all duration-300 hover:bg-content-subtle/10 hover:no-underline`}
                  telemetryInteractionName={control.telemetryInteractionName}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();

                    // Start a short timeout after which we will trigger the click action
                    setTimeout(() => {
                      control.onClick(event);
                      setTimeout(() => {
                        // After the operatiion is complete, set the processing state back to false and close the menu
                        close();
                        setProcessingControlLabel(null);
                      }, 400);
                    }, 50);

                    // Request an animation frame and set processing state to true; this ensures the component displays the loading
                    // state before starting processing which may block the main thread
                    requestAnimationFrame(() => {
                      setProcessingControlLabel(control.label);
                    });
                  }}
                >
                  {processingControlLabel === control.label && <LoadingSpinner />}
                  <span>{control.label}</span>
                </Button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
};
